import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { businessConfig } from "@/config";
import Header from "@/components/booking/Header";
import Footer from "@/components/booking/Footer";
import BookingWizard from "@/components/booking/BookingWizard";
import { fetchSettings, fetchServices, fetchAvailability } from "@/lib/api";
import { ArrowLeft, Loader2 } from "lucide-react";

export default function BookingPage() {
  const [settings, setSettings] = useState(null);
  const [services, setServices] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [s, sv, av] = await Promise.all([
          fetchSettings(),
          fetchServices(),
          fetchAvailability(),
        ]);
        if (!mounted) return;
        setSettings(s);
        setServices(sv);
        setAvailability(av);
      } catch (e) {
        if (mounted) setError("We couldn't load the booking form. Please refresh.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
    // Mount-only fetch — all referenced functions are stable module imports.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const display = settings || {
    business_name: businessConfig.name,
    business_phone: businessConfig.phone,
    business_email: businessConfig.email,
    business_address: businessConfig.address,
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="pp-card p-12 flex flex-col items-center gap-3" data-testid="page-loading">
          <Loader2 className="w-7 h-7 animate-spin" style={{ color: "var(--pp-indigo)" }} />
          <p className="text-sm" style={{ color: "var(--pp-muted)" }}>Loading booking form…</p>
        </div>
      );
    }
    if (error) {
      return (
        <div className="pp-card p-8" data-testid="page-error">
          <p style={{ color: "var(--pp-error)" }}>{error}</p>
        </div>
      );
    }
    return (
      <BookingWizard
        services={services}
        availability={availability}
        settings={display}
      />
    );
  };

  return (
    <div className="pp-bg min-h-screen flex flex-col" data-testid="booking-page">
      <Header settings={display} />
      <main className="flex-1 w-full flex justify-center px-4 pb-6">
        <div className="w-full max-w-[640px] flex flex-col gap-5">
          {renderContent()}
          <div className="flex justify-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full hover:underline"
              style={{ color: "var(--pp-indigo)" }}
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </Link>
          </div>
        </div>
      </main>
      <Footer settings={display} />
    </div>
  );
}
